import { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { apiClient } from '../../lib/api';
import { StandupClientRow, StandupDigest } from '../../types/standup';
import { Spinner } from '../ui';
import { formatDateTime } from '../../utils/dateUtils';
import { WorkspaceBriefPanel } from '../standup/standupParts';
import { AccountsView } from './AccountsView';
import { RepliesView } from './RepliesView';
import { CampaignsView } from '../campaigns-overview/CampaignsView';

type Lens = 'clients' | 'campaigns' | 'replies';

const LENSES: { key: Lens; label: string }[] = [
  { key: 'clients', label: 'Clients' },
  { key: 'campaigns', label: 'Campaigns' },
  { key: 'replies', label: 'Replies' },
];

const TOTAL_LABELS: { key: string; label: string }[] = [
  { key: 'sent', label: 'Sent' },
  { key: 'opens', label: 'Opens' },
  { key: 'replies', label: 'Replies' },
  { key: 'meetings', label: 'Meetings' },
  { key: 'li_replied', label: 'LI Replied' },
];

/** Compound hub: standup header band + Clients / Campaigns / Replies lens switcher. */
export function ClientsHub() {
  const [standup, setStandup] = useState<StandupDigest | null>(null);
  const [standupLoading, setStandupLoading] = useState(true);

  const [lens, setLens] = useState<Lens>('clients');
  // Keep-alive: a lens is mounted on first visit and stays mounted (hidden when inactive) so
  // switching tabs never refetches/loses state. 'clients' is mounted from the start.
  const [mounted, setMounted] = useState<Record<Lens, boolean>>({
    clients: true,
    campaigns: false,
    replies: false,
  });
  const [focusEntity, setFocusEntity] = useState<string | null>(null);

  const switchLens = useCallback((key: Lens) => {
    setMounted((m) => (m[key] ? m : { ...m, [key]: true }));
    setLens(key);
  }, []);

  const openClient = useCallback((id: string) => {
    setFocusEntity(id);
    setLens('clients');
  }, []);

  // Fix 2 — stable callback so AccountsView's seed effect doesn't re-fire.
  const clearSeed = useCallback(() => setFocusEntity(null), []);

  // Fix 1 — memoized fetch so the RefreshCw button can call it directly.
  const loadStandup = useCallback(() => {
    setStandupLoading(true);
    apiClient
      .getStandup()
      .then(setStandup)
      .catch(() => {
        /* standup is additive — header degrades gracefully, lenses still render */
      })
      .finally(() => setStandupLoading(false));
  }, []);

  useEffect(() => {
    loadStandup();
  }, [loadStandup]);

  // Fix 4 — memoize lookup so it isn't rebuilt on every render.
  const standupByEntity = useMemo<Record<string, StandupClientRow>>(
    () => (standup ? Object.fromEntries(standup.clients.map((c) => [c.entity_id, c])) : {}),
    [standup],
  );

  const today = new Date().toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

  return (
    <>
      {/* ── 24h standup band ────────────────────────────────────────────────── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-13 font-medium uppercase tracking-wider text-neutral-400">
              Last 24h
            </span>
            <span className="text-xs text-neutral-400">
              {today}
              {standup?.generated_at && !standupLoading
                ? ` · as of ${formatDateTime(standup.generated_at)}`
                : ''}
            </span>
          </div>
          <button
            onClick={loadStandup}
            aria-label="Refresh standup"
            className="flex items-center gap-1.5 text-xs font-medium text-neutral-500 hover:text-neutral-800 px-2.5 py-1 rounded-md hover:bg-neutral-100 transition"
          >
            {standupLoading ? (
              <Spinner size="sm" />
            ) : (
              <RefreshCw size={13} className="text-neutral-400" />
            )}
            Refresh
          </button>
        </div>

        {/* Premium stat tiles */}
        {standup ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {TOTAL_LABELS.map(({ key, label }) => (
              <div
                key={key}
                className="bg-white border border-neutral-200 rounded-lg shadow-card px-4 py-3"
              >
                <div className="text-2xl font-semibold leading-none text-neutral-900 tabular-nums">
                  {standup.totals_24h[key] ?? 0}
                </div>
                <div className="mt-1.5 text-2xs font-medium uppercase tracking-wide text-neutral-400">
                  {label}
                </div>
              </div>
            ))}
          </div>
        ) : !standupLoading ? (
          <p className="text-sm text-neutral-400">24h totals unavailable</p>
        ) : null}

        {/* Workspace brief (collapsible) */}
        {standup?.workspace_brief?.brief ? (
          <div className="mt-4">
            <WorkspaceBriefPanel
              brief={standup.workspace_brief.brief}
              generatedAt={standup.workspace_brief.generated_at ?? ''}
            />
          </div>
        ) : null}

        {/* Lens switcher (segmented pill) */}
        <div
          className="inline-flex items-center gap-1 bg-neutral-100 rounded-full p-1 mt-5"
          role="tablist"
          aria-label="View lens"
        >
          {LENSES.map(({ key, label }) => (
            <button
              key={key}
              role="tab"
              aria-selected={lens === key}
              aria-controls="lens-panel"
              onClick={() => switchLens(key)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition ${
                lens === key
                  ? 'bg-white text-primary-700 shadow-card'
                  : 'text-neutral-500 hover:text-neutral-800'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Lens content (keep-alive: mounted lenses stay mounted, hidden when inactive) ──── */}
      <div id="lens-panel" role="tabpanel">
        {mounted.clients ? (
          <div className={lens === 'clients' ? '' : 'hidden'}>
            <AccountsView
              seedEntityId={focusEntity}
              onSeedConsumed={clearSeed}
              standupByEntity={standupByEntity}
            />
          </div>
        ) : null}
        {mounted.campaigns ? (
          <div className={lens === 'campaigns' ? '' : 'hidden'}>
            <CampaignsView onOpenClient={openClient} />
          </div>
        ) : null}
        {mounted.replies ? (
          <div className={lens === 'replies' ? '' : 'hidden'}>
            <RepliesView />
          </div>
        ) : null}
      </div>
    </>
  );
}
